import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapTrifold, Target, Timer, Shield, Users, CheckCircle,
  ArrowRight, Crosshair, Mountains, Buildings, Ruler,
  HardHat, Blueprint, WaveSawtooth, Star, TrendUp, 
  Handshake, Certificate, Clock, Compass, Eye
} from '@phosphor-icons/react';
import { USMap } from '../components/USMap';
import { LazyMount } from '../components/LazyMount';
import { HomeSearchIntents } from '../components/HomeSearchIntents';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SEO, pageSEO } from '../components/SEO';
import { HUB_ALTERNATES } from '../data/countryMarkets';
import { SoftwareShowcase } from '../components/SoftwareShowcase';

// Scroll reveal hook
const useScrollReveal = () => {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

    reveals.forEach(el => observer.observe(el));

    return () => reveals.forEach(el => observer.unobserve(el));
  }, []);
};

const services = [
  { icon: Buildings, name: 'ALTA/NSPS Surveys', desc: 'Commercial land title surveys meeting ALTA standards', link: '/services/alta-nsps' },
  { icon: Mountains, name: 'Topographic Surveys', desc: 'Elevation and contour mapping for site development', link: '/services/topographic' },
  { icon: Crosshair, name: 'Boundary Surveys', desc: 'Property line determination and documentation', link: '/services/boundary' },
  { icon: WaveSawtooth, name: 'Utility Surveys', desc: 'Underground and overhead utility mapping', link: '/services/utility' },
  { icon: HardHat, name: 'Construction Staking', desc: 'Precise field layout for construction projects', link: '/services/construction-staking' },
  { icon: Blueprint, name: 'As-Built Surveys', desc: 'Post-construction documentation', link: '/services/as-built' },
  { icon: Ruler, name: 'Final Plats', desc: 'Subdivision plats for property division', link: '/services/final-plats' },
];

const valueProps = [
  { icon: Timer, title: '24-Hour Turnaround', desc: 'Same-day or next-day delivery capability for urgent projects' },
  { icon: Target, title: 'Survey-Focused Experts', desc: 'Deep understanding of US surveying standards and requirements' },
  { icon: Users, title: 'Scalable Team Support', desc: 'Handle bulk projects without hiring additional overhead' },
  { icon: Shield, title: 'QC-Driven Workflow', desc: 'Multi-level quality assurance on every project deliverable' },
];

const stats = [
  { value: '500+', label: 'Projects Completed', icon: Certificate },
  { value: '50', label: 'States Covered', icon: MapTrifold },
  { value: '98%', label: 'Client Satisfaction', icon: Star },
  { value: '24hr', label: 'Average Turnaround', icon: Clock },
];

const software = ['AutoCAD', 'Civil 3D', 'Carlson Survey', 'IntelliCAD', 'ProgeCAD', 'BricsCAD'];

const surveyTypes = [
  'All Survey Types',
  'ALTA/NSPS Survey',
  'Boundary Survey',
  'Topographic Survey',
  'Subdivision Plat',
  'Construction Survey',
  'As-Built Survey',
  'Utility Mapping'
];

const testimonials = [
  { 
    quote: "SurveysMaps has been our go-to drafting partner for 3 years. Their turnaround time and accuracy are unmatched.",
    author: "Michael R.",
    company: "Texas Land Surveyors LLC",
    rating: 5
  },
  { 
    quote: "Professional team that understands surveying standards. They've helped us scale without adding staff.",
    author: "Sarah K.",
    company: "Precision Survey Group",
    rating: 5
  },
  { 
    quote: "Consistent quality on every project. From boundary to ALTA, they handle it all.",
    author: "James T.",
    company: "California Survey Associates",
    rating: 5
  },
];

export default function Home() {
  const [selectedSurveyType, setSelectedSurveyType] = useState('All Survey Types');
  const [selectedState, setSelectedState] = useState(null);
  
  useScrollReveal();

  return (
    <div data-testid="home-page">
      <SEO {...pageSEO.home} alternates={HUB_ALTERNATES} preloadImage="/hero-survey-cad.webp" />
      
      {/* Hero Section - Dark Mode */}
      <section 
        className="relative min-h-[90vh] flex items-center bg-[#0A0A23]"
        data-testid="hero-section"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/hero-survey-cad.webp')` }}
        >
          <div className="hero-overlay absolute inset-0"></div>
        </div>

        <div className="container-custom relative z-10 py-24">
          <div className="max-w-4xl">
            <span className="label-mono text-[#06D6E0] mb-4 block animate-fade-in-down" data-testid="hero-label">
              CAD Drafting for Land Surveyors
            </span>
            <h1 className="heading-1 text-white mb-6" data-testid="hero-headline">
              Land Survey CAD Drafting Services Provider for Surveyors
            </h1>
            <p className="hero-tagline text-[#06D6E0] font-medium mb-4" data-testid="hero-tagline">Outsourced survey drafting for the USA, Canada, UK & Australia — 24–48 hr turnaround</p>
            <p className="text-lg md:text-xl text-zinc-400 mb-10 max-w-2xl leading-relaxed animate-fade-in-up delay-200" data-testid="hero-subheadline">
              Fast, accurate, scalable drafting support for land surveyors in the USA, Canada, UK and Australia — every survey type under one roof.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300" data-testid="hero-cta">
              <Link to="/quote" className="btn-primary inline-flex items-center gap-2 hover-scale animate-pulse-glow" data-testid="hero-cta-quote">
                Get Instant Quote <ArrowRight size={20} />
              </Link>
              <Link to="/get-started" className="btn-secondary hover-lift" data-testid="hero-cta-getstarted">
                Upload Your Files
              </Link>
            </div>
          </div>
          
          {/* Floating Stats */}
          <div className="hidden lg:block absolute bottom-20 right-10 animate-float">
            <div className="bg-white/10 backdrop-blur-lg p-6 border border-white/20">
              <div className="text-4xl font-black text-[#3B82F6]" style={{ fontFamily: 'Outfit' }}>500+</div>
              <div className="text-white/80 text-sm">Projects Delivered</div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6]" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center reveal" style={{ animationDelay: `${idx * 100}ms` }}>
                <stat.icon size={32} weight="light" className="text-white/80 mx-auto mb-2" />
                <div className="text-3xl md:text-4xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
                  {stat.value}
                </div>
                <div className="text-white/80 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HomeSearchIntents />

      {/* Intro Section */}
      <section className="section-padding bg-white" data-testid="intro-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="reveal">
              <span className="label-mono text-[#06D6E0] mb-4 block">Who We Are</span>
              <h2 className="heading-2 mb-6">Land Survey CAD Drafting Services for US Surveying Firms</h2>
              <p className="body-text mb-6">
                We provide specialized CAD drafting services exclusively for land surveyors and surveying firms. Our workflows are designed to match real-world surveying standards, ensuring every drawing aligns with field data, local codes, and client expectations.
              </p>
              <p className="body-text mb-8">
                From boundary surveys to complex ALTA drawings, we deliver production-ready CAD files with precision and speed.
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 text-[#3B82F6] font-semibold hover:gap-3 transition-all text-hover-slide">
                Learn More About Us <ArrowRight size={20} />
              </Link>
            </div>
            <div className="relative reveal delay-200">
              <img 
                src="https://images.unsplash.com/photo-1770822662903-aa69f6aa9b03?crop=entropy&cs=srgb&fm=webp&w=1200&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxsYW5kJTIwc3VydmV5b3IlMjBlcXVpcG1lbnQlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3NDk0NjgzMXww&ixlib=rb-4.1.0&q=75"
                alt="Land surveyor with GPS equipment collecting field data for survey CAD drafting" width="1200" height="800" loading="lazy" decoding="async"
                className="w-full border border-zinc-200 hover-lift"
                data-testid="intro-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#3B82F6] text-white p-6 animate-fade-in-up delay-400">
                <div className="text-4xl font-black" style={{ fontFamily: 'Outfit' }}>10+</div>
                <div className="text-sm">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="section-padding bg-[#F3F0EB] tech-grid" data-testid="value-props-section">
        <div className="container-custom">
          <div className="text-center mb-16 reveal">
            <span className="label-mono text-[#06D6E0] mb-4 block">Why Choose Us</span>
            <h2 className="heading-2">Built for Surveying Professionals</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200">
            {valueProps.map((prop, idx) => (
              <div 
                key={idx} 
                className="bg-white p-8 hover:bg-[#F3F0EB] transition-colors reveal hover-lift"
                style={{ animationDelay: `${idx * 100}ms` }}
                data-testid={`value-prop-${idx}`}
              >
                <prop.icon size={40} weight="light" className="text-[#3B82F6] mb-4" />
                <h3 className="heading-3 text-lg mb-2">{prop.title}</h3>
                <p className="text-zinc-600 text-sm">{prop.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white survey-pattern" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-16 reveal">
            <span className="label-mono text-[#06D6E0] mb-4 block">Our Services</span>
            <h2 className="heading-2">Complete CAD Drafting Solutions</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger-children">
            {services.map((service, idx) => (
              <Link 
                key={idx}
                to={service.link}
                className="bg-white p-6 border border-zinc-200 hover-lift group"
                data-testid={`service-card-${idx}`}
              >
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center mb-4 group-hover:bg-[#3B82F6] transition-colors">
                  <service.icon size={24} weight="light" className="text-[#3B82F6] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-semibold text-zinc-900 mb-2 group-hover:text-[#3B82F6] transition-colors">{service.name}</h3>
                <p className="text-zinc-600 text-sm mb-4">{service.desc}</p>
                <span className="inline-flex items-center gap-1 text-[#3B82F6] text-sm font-medium group-hover:gap-2 transition-all">
                  Learn More <ArrowRight size={16} />
                </span>
              </Link>
            ))}
            {/* View All Services Card */}
            <Link 
              to="/services"
              className="bg-[#0A0A23] p-6 hover:bg-[#0F0B2E] transition-colors group flex flex-col justify-center items-center text-center"
              data-testid="view-all-services"
            >
              <Compass size={36} weight="light" className="text-[#3B82F6] mb-4" />
              <h3 className="font-semibold text-white mb-2">View All Services</h3>
              <p className="text-zinc-400 text-sm mb-4">16+ survey types covered</p>
              <span className="inline-flex items-center gap-1 text-[#3B82F6] text-sm font-medium group-hover:gap-2 transition-all">
                See Full List <ArrowRight size={16} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* US Map Section */}
      <section className="section-padding bg-[#F3F0EB]" data-testid="map-section">
        <div className="container-custom">
          <div className="text-center mb-8 reveal">
            <span className="label-mono text-[#06D6E0] mb-4 block">Coverage</span>
            <h2 className="heading-2 mb-4">Nationwide Coverage Across the United States</h2>
            <p className="body-text max-w-2xl mx-auto">
              We serve surveying professionals across all 50 states. Click on a state to see county-level details and project examples.
            </p>
          </div>

          {/* Filter */}
          <div className="flex justify-center mb-8 reveal delay-200">
            <div className="w-64">
              <Select 
                value={selectedSurveyType} 
                onValueChange={setSelectedSurveyType}
              >
                <SelectTrigger className="border-zinc-200" data-testid="map-filter-select">
                  <SelectValue placeholder="Filter by survey type" />
                </SelectTrigger>
                <SelectContent>
                  {surveyTypes.map((type) => (
                    <SelectItem key={type} value={type} data-testid={`filter-option-${type.toLowerCase().replace(/\s+/g, '-')}`}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="reveal delay-300">
            <LazyMount minHeight={560} fallback={<Link to="/service-areas" className="text-[#3B82F6] underline">Browse all 50 US states we serve</Link>}>
              <USMap 
                selectedType={selectedSurveyType === 'All Survey Types' ? null : selectedSurveyType}
                onStateSelect={setSelectedState}
              />
            </LazyMount>
          </div>
        </div>
      </section>

      {/* Software Section (tabbed showcase) */}
      <SoftwareShowcase />

      {/* Testimonials */}
      <section className="section-padding bg-[#F3F0EB] topo-pattern" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center mb-12 reveal">
            <span className="label-mono text-[#06D6E0] mb-4 block">Testimonials</span>
            <h2 className="heading-2">What Our Clients Say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <div 
                key={idx} 
                className="bg-white p-8 border border-zinc-200 hover-lift reveal"
                style={{ animationDelay: `${idx * 150}ms` }}
                data-testid={`testimonial-${idx}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} weight="fill" className="text-[#FFD700]" />
                  ))}
                </div>
                <p className="text-zinc-700 mb-6 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold text-zinc-900">{testimonial.author}</p>
                  <p className="text-sm text-zinc-500">{testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Turnaround */}
      <section className="section-padding bg-[#0A0A23] text-white" data-testid="turnaround-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="reveal">
              <span className="label-mono text-[#06D6E0] mb-4 block">Fast Delivery</span>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
                Quick Turnaround When You Need It
              </h2>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                We understand deadlines in surveying projects are critical. Our streamlined workflow ensures your projects are delivered on time, every time.
              </p>
              <ul className="space-y-4">
                {[
                  'Same-day or next-day delivery options',
                  'Parallel drafting for large projects',
                  'Revision turnaround within hours'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 reveal" style={{ animationDelay: `${idx * 100}ms` }}>
                    <CheckCircle size={24} weight="fill" className="text-[#3B82F6]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative reveal delay-200">
              <img 
                src="https://images.unsplash.com/photo-1764740109279-c7a8abd78821?crop=entropy&cs=srgb&fm=webp&w=1200&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxhcmNoaXRlY3R1cmUlMjBibHVlcHJpbnRzJTIwZW5naW5lZXJpbmclMjBkZXNrfGVufDB8fHx8MTc3NDk0NjgzMnww&ixlib=rb-4.1.0&q=75"
                alt="Survey CAD drawings and blueprints on a drafting desk" width="1200" height="800" loading="lazy" decoding="async"
                className="w-full border border-zinc-700 hover-lift"
                data-testid="turnaround-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-white" data-testid="process-section">
        <div className="container-custom">
          <div className="text-center mb-12 reveal">
            <span className="label-mono text-[#06D6E0] mb-4 block">How It Works</span>
            <h2 className="heading-2">Simple 5-Step Process</h2>
          </div>
          <div className="grid md:grid-cols-5 gap-4">
            {[
              { step: '01', title: 'Upload', desc: 'Submit your field data and files' },
              { step: '02', title: 'Review', desc: 'We review scope and requirements' },
              { step: '03', title: 'Quote', desc: 'Receive detailed project quote' },
              { step: '04', title: 'Draft', desc: 'Our team creates your drawings' },
              { step: '05', title: 'Deliver', desc: 'Receive production-ready files' },
            ].map((item, idx) => (
              <div 
                key={idx} 
                className="text-center p-6 border border-zinc-200 hover-lift reveal"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                <div className="text-4xl font-black text-[#3B82F6] mb-2" style={{ fontFamily: 'Outfit' }}>
                  {item.step}
                </div>
                <h3 className="font-semibold text-zinc-900 mb-1">{item.title}</h3>
                <p className="text-sm text-zinc-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6]" data-testid="cta-section">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-white mb-6 reveal" style={{ fontFamily: 'Outfit' }}>
            Ready to Scale Your Survey Operations?
          </h2>
          <p className="text-white/80 mb-10 max-w-2xl mx-auto text-lg reveal delay-100">
            Start outsourcing your CAD drafting today and scale your survey operations without hiring overhead.
          </p>
          <div className="flex flex-wrap justify-center gap-4 reveal delay-200">
            <Link to="/get-started" className="bg-white text-zinc-900 font-semibold px-8 py-4 hover:bg-zinc-100 transition-colors inline-flex items-center gap-2 hover-scale" data-testid="cta-getstarted">
              Get Started <ArrowRight size={20} />
            </Link>
            <Link to="/contact" className="border-2 border-white text-white font-semibold px-8 py-4 hover:bg-white/10 transition-colors" data-testid="cta-contact">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
